import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useCurrency } from '../components/CurrencyContext';
import Skeleton from '../components/shared/ui/Skeleton';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import PackageImageCarousel from '../components/packages/PackageImageCarousel';
import PackageCard from '../components/packages-listing/PackageCard';
import EnquiryModal from '../components/packages-listing/EnquiryModal';
import PackageEnquiryForm from '../components/packages-listing/PackageEnquiryForm';
import { getPackageBySlug } from '../api/packagesListing';
import useIsMobile from '../../hooks/useIsMobile';
import MobileBottomNav from '../../mobile/components/MobileBottomNav';
import MobileHeader from '../../mobile/components/MobileHeader';

const sectionCardClass =
  'rounded-[30px] border border-[#dde3ef] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)] md:p-8';

const factItems = (pkg) => {
  const items = [
    pkg?.days || pkg?.duration_days ? `${pkg.days || pkg.duration_days} days` : null,
    pkg?.nights || pkg?.duration_nights ? `${pkg.nights || pkg.duration_nights} nights` : null,
    pkg?.group_size || pkg?.max_persons || pkg?.max_guests
      ? `${pkg.group_size || pkg.max_persons || pkg.max_guests} guests`
      : null,
    pkg?.themes?.[0]?.name || pkg?.theme_name || null,
  ];
  return items.filter(Boolean);
};

const formatDateLabel = (value) => {
  if (!value) return 'Flexible';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const normalizeTextList = (value) => {
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/\r?\n|,/)
      : [];

  return source
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        return String(
          item.label ||
            item.name ||
            item.title ||
            item.value ||
            item.item ||
            item.category ||
            ''
        ).trim();
      }
      return String(item || '').trim();
    })
    .filter(Boolean);
};

const normalizeItinerary = (value) => {
  const rows = Array.isArray(value) ? value : [];
  const seen = new Set();

  return rows
    .map((item, index) => {
      const dayNumber = item?.day_number || item?.dayNumber || index + 1;
      const dayTitle = item?.day_title || item?.dayTitle || `Day ${dayNumber}`;
      const dayDescription = item?.day_description || item?.description || '';
      const stayInfo = item?.stay_info || item?.stayInfo || '';
      const mealsInfo = item?.meals_info || item?.mealsInfo || '';
      const imageUrl = item?.image_url || item?.imageUrl || '';
      const signature = JSON.stringify([
        dayNumber,
        String(dayTitle).trim(),
        String(dayDescription).trim(),
        String(stayInfo).trim(),
        String(mealsInfo).trim(),
        String(imageUrl).trim(),
      ]);

      if (seen.has(signature)) return null;
      seen.add(signature);

      return {
        ...item,
        day_number: dayNumber,
        day_title: dayTitle,
        day_description: dayDescription,
        stay_info: stayInfo,
        meals_info: mealsInfo,
        image_url: imageUrl,
      };
    })
    .filter(Boolean);
};

const PackageDetail = () => {
  const isMobile = useIsMobile();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { formatCurrency } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enquiryPkg, setEnquiryPkg] = useState(null);
  const [openDay, setOpenDay] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    getPackageBySlug(slug)
      .then((res) => {
        if (!active) return;
        const next = res?.data || res || null;
        setData(next);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'Unable to load package.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const title = data.seo_title || data.title || 'Travel package';
    const description =
      data.seo_description || data.short_description || data.tagline || 'Discover a curated Homevilas journey.';
    document.title = title;

    const upsertMeta = (name, content, attr = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    upsertMeta('description', description);
    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', description, 'property');
    if (data.hero_image_url || data.hero_image) {
      upsertMeta('og:image', data.hero_image_url || data.hero_image, 'property');
    }
  }, [data]);

  const enquiryDefaults = useMemo(
    () => ({
      travelDate: searchParams.get('startDate') || searchParams.get('travelDate') || '',
      travellers: searchParams.get('travellers') || '',
      budget: searchParams.get('budgetMax') || searchParams.get('budget') || '',
    }),
    [searchParams]
  );

  const listUrl = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('from');
    const query = params.toString();
    return query ? `/packages/list?${query}` : '/packages/list';
  }, [searchParams]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12">
        <Skeleton className="h-[360px] w-full rounded-[36px]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="rounded-[32px] border border-rose-100 bg-rose-50 p-6 text-sm text-rose-700">
          {error || 'Package not found.'}
        </div>
      </div>
    );
  }

  const priceLabel = formatCurrency(data.price_from || data.price_per_person || 0, data.currency || 'INR');
  const categories = normalizeTextList(
    data.categories ||
      data.category_list ||
      data.categoryList ||
      data.custom_sections?.categories ||
      data.themes
  );
  const highlights = Array.isArray(data.highlights) ? data.highlights : [];
  const inclusions = Array.isArray(data.inclusions) ? data.inclusions : [];
  const exclusions = Array.isArray(data.exclusions) ? data.exclusions : [];
  const itinerary = normalizeItinerary(data.itinerary);
  const reviews = Array.isArray(data.reviews) ? data.reviews : [];
  const quickFacts = factItems(data);
  const gallery = Array.isArray(data.gallery) && data.gallery.length
    ? data.gallery
    : data.images || [data.hero_image_url || data.hero_image].filter(Boolean);
  const seasonalNote =
    data.seasonal_note ||
    data.seasonalNote ||
    data.seasonal_text ||
    data.seasonalText ||
    data.custom_sections?.seasonal_note ||
    data.custom_sections?.seasonalNote ||
    '';

  return (
    <div className="bg-[#f7f4ee] text-slate-900">
      {isMobile ? <MobileHeader showBack backTo="/packages" /> : null}
      <section className="overflow-hidden bg-[linear-gradient(120deg,#071938_0%,#0c2e52_45%,#0f766e_100%)] text-white">
        <div className="mx-auto max-w-7xl px-4 py-12 md:py-16">
          <Breadcrumbs
            items={[
              { label: 'Packages', to: '/packages' },
              { label: 'Listing', to: listUrl },
              { label: data.title },
            ]}
            className="text-white/65"
            linkClassName="text-white/90"
            separatorClassName="text-white/35"
          />
          <div className="mt-5 grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[#d7b36a]">Homevilas Journeys</p>
              <h1 className="mt-4 font-homevilas text-4xl font-semibold leading-tight md:text-6xl">
                {data.title}
              </h1>
              <p className="mt-4 max-w-3xl text-base leading-7 text-white/78 md:text-lg">
                {data.tagline || data.subtitle || data.short_description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-white/75">
                <span>{data.destination_name || data.destination_city || 'Curated destination'}</span>
                {data.rating ? <span className="h-1 w-1 rounded-full bg-white/50" /> : null}
                {data.rating ? <span>{Number(data.rating || 0).toFixed(1)} rating</span> : null}
                {data.review_count ? <span className="h-1 w-1 rounded-full bg-white/50" /> : null}
                {data.review_count ? <span>{data.review_count} reviews</span> : null}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {quickFacts.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white"
                  >
                    {item}
                  </span>
                ))}
                {data.badge_featured || data.is_featured ? (
                  <span className="rounded-full border border-[#d7b36a]/20 bg-[#d7b36a]/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#f7d89e]">
                    Featured
                  </span>
                ) : null}
                {data.badge_trending || data.is_trending ? (
                  <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                    Trending
                  </span>
                ) : null}
              </div>
            </div>

            <div className="rounded-[32px] border border-white/12 bg-white/10 p-6 shadow-[0_20px_60px_rgba(0,0,0,0.22)] backdrop-blur">
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-white/55">From</p>
              <div className="mt-2 flex items-end gap-3">
                <p className="text-4xl font-semibold text-white">{priceLabel}</p>
                {data.original_price ? (
                  <p className="pb-1 text-sm text-white/45 line-through">
                    {formatCurrency(data.original_price, data.currency || 'INR')}
                  </p>
                ) : null}
              </div>
              <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">
                Per person
              </p>
              <p className="mt-5 text-sm text-white/70">
                Availability: {formatDateLabel(data.availability_start)} to {formatDateLabel(data.availability_end)}
              </p>
              <p className="mt-2 text-sm text-white/70">
                Group size: {data.group_size || data.max_persons || data.max_guests || 'Flexible'}
              </p>
              <a
                href="#package-enquiry"
                className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#081b3a] transition hover:bg-[#f2f4f8]"
              >
                {data.enquire_button_label || 'Enquire now'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:py-14">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.65fr)_360px]">
          <div className="space-y-8">
            <PackageImageCarousel images={gallery} title={data.title} />

            <section className={sectionCardClass}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Overview</p>
                  <h2 className="mt-2 font-homevilas text-3xl font-semibold text-[#0d213f] md:text-4xl">
                    A refined journey through {data.destination_name || data.destination_city || 'your destination'}
                  </h2>
                </div>
                {data.request_only ? (
                  <span className="rounded-full bg-[#f4e7cf] px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#8a6428]">
                    Request only
                  </span>
                ) : null}
              </div>
              <div className="mt-5 space-y-4 text-[15px] leading-8 text-slate-600">
                {data.overview ? <p>{data.overview}</p> : null}
                {data.full_description && data.full_description !== data.overview ? <p>{data.full_description}</p> : null}
              </div>
            </section>

            {highlights.length ? (
              <section className={sectionCardClass}>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Highlights</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  {highlights.map((item) => (
                    <div
                      key={item}
                      className="rounded-full border border-[#e5d5b5] bg-[#fbf4e6] px-4 py-2 text-sm font-semibold text-[#805c22]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {categories.length ? (
              <section className={sectionCardClass}>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Categories</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  {categories.map((item) => (
                    <div
                      key={item}
                      className="rounded-full border border-[#dde3ef] bg-[#f7f9fc] px-4 py-2 text-sm font-semibold text-[#1f3557]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {(inclusions.length || exclusions.length) ? (
              <section className="grid gap-6 md:grid-cols-2">
                <div className={sectionCardClass}>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Inclusions</p>
                  <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                    {inclusions.length ? (
                      inclusions.map((item) => (
                        <li key={item} className="flex gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-[#0f766e]" />
                          <span>{item}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-slate-400">Customizable based on your departure.</li>
                    )}
                  </ul>
                </div>
                <div className={sectionCardClass}>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Exclusions</p>
                  <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                    {exclusions.length ? (
                      exclusions.map((item) => (
                        <li key={item} className="flex gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-[#d48a1f]" />
                          <span>{item}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-slate-400">Flights, extras, and incidental expenses are quoted separately.</li>
                    )}
                  </ul>
                </div>
              </section>
            ) : null}

            {itinerary.length ? (
              <section className={sectionCardClass}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Itinerary</p>
                    <h3 className="mt-2 font-homevilas text-3xl font-semibold text-[#0d213f]">Day-by-day flow</h3>
                  </div>
                  <p className="text-sm text-slate-400">{itinerary.length} planned moments</p>
                </div>
                <div className="mt-5 space-y-4">
                  {itinerary.map((item, itineraryIndex) => {
                    const isOpen = openDay === itineraryIndex;
                    return (
                      <div
                        key={item.id || `${item.day_number}-${itineraryIndex}`}
                        className="overflow-hidden rounded-[26px] border border-[#dde3ef] bg-[#fbfcfe]"
                      >
                        <button
                          type="button"
                          onClick={() => setOpenDay(isOpen ? -1 : itineraryIndex)}
                          className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left"
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                              <span className="rounded-full bg-[#081b3a] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                                Day {item.day_number || itineraryIndex + 1}
                              </span>
                              <h4 className="text-lg font-semibold text-[#15243d]">{item.day_title}</h4>
                            </div>
                            {!isOpen ? (
                              <p className="mt-2 line-clamp-2 text-sm text-slate-500">
                                {item.day_description || item.description}
                              </p>
                            ) : null}
                          </div>
                          <i className={`bi ${isOpen ? 'bi-dash-lg' : 'bi-plus-lg'} text-slate-500`} />
                        </button>
                        {isOpen ? (
                          <div className="border-t border-[#e6ebf2] px-5 py-5">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.day_title}
                                className="mb-5 h-48 w-full rounded-[22px] object-cover"
                              />
                            ) : null}
                            <p className="text-sm leading-7 text-slate-600">
                              {item.day_description || item.description}
                            </p>
                            {(item.meals_info || item.stay_info) ? (
                              <div className="mt-4 flex flex-wrap gap-3">
                                {item.meals_info ? (
                                  <span className="rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                                    Meals: {item.meals_info}
                                  </span>
                                ) : null}
                                {item.stay_info ? (
                                  <span className="rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                                    Stay: {item.stay_info}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {seasonalNote ? (
              <section className="rounded-[30px] border border-[#dfe3ea] bg-[linear-gradient(135deg,#fffdf7_0%,#f5efe2_100%)] p-6 shadow-[0_16px_40px_rgba(15,23,42,0.04)] md:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9b8145]">Travel note</p>
                <p className="mt-3 text-[15px] leading-8 text-[#5a4a2d]">{seasonalNote}</p>
              </section>
            ) : null}

            {reviews.length ? (
              <section className={sectionCardClass}>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Traveller stories</p>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  {reviews.map((review) => (
                    <article
                      key={review.id}
                      className="rounded-[24px] border border-[#e3e8f2] bg-[#fbfcfe] p-5"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[#13223b]">{review.customer_name}</p>
                          <p className="text-xs text-slate-500">{review.customer_location || 'Homevilas guest'}</p>
                        </div>
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                          {Number(review.rating || 0).toFixed(1)}
                        </span>
                      </div>
                      <p className="mt-4 text-sm leading-7 text-slate-600">{review.review_text}</p>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-[30px] border border-[#dde3ef] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Package facts</p>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <div className="flex items-start justify-between gap-3">
                  <span>Duration</span>
                  <span className="font-semibold text-slate-900">
                    {data.days || data.duration_days || 0}D / {data.nights || data.duration_nights || 0}N
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span>Destination</span>
                  <span className="text-right font-semibold text-slate-900">
                    {data.destination_name || data.destination_city || '-'}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span>Guests</span>
                  <span className="font-semibold text-slate-900">
                    {data.group_size || data.max_persons || data.max_guests || 'Flexible'}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <span>Theme</span>
                  <span className="text-right font-semibold text-slate-900">
                    {data.themes?.map((item) => item.name).slice(0, 2).join(', ') || data.theme_name || '-'}
                  </span>
                </div>
              </div>
            </div>

            <div id="package-enquiry" className="rounded-[30px] border border-[#d4dde8] bg-white p-6 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
              <div className="border-b border-[#eef2f7] pb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Enquiry</p>
                <h3 className="mt-2 font-homevilas text-3xl font-semibold text-[#0d213f]">Plan this journey</h3>
                <p className="mt-2 text-sm leading-7 text-slate-500">
                  Share your dates and preferences. The travel desk will respond with pricing, availability, and custom options.
                </p>
              </div>
              <div className="mt-5">
                <PackageEnquiryForm
                  pkg={data}
                  source="DETAILS"
                  initialValues={enquiryDefaults}
                  compact
                />
              </div>
            </div>

            <div className="rounded-[30px] border border-[#dbe1eb] bg-[#0d1f3b] p-6 text-white shadow-[0_20px_50px_rgba(8,27,58,0.18)]">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/55">Need a tailored version?</p>
              <p className="mt-3 text-sm leading-7 text-white/75">
                Convert this package into a custom celebration, family escape, or multi-city journey with private additions.
              </p>
              <button
                type="button"
                onClick={() => setEnquiryPkg(data)}
                className="mt-5 rounded-full bg-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#081b3a]"
              >
                Request custom package
              </button>
            </div>
          </aside>
        </div>
      </section>

      {Array.isArray(data.related) && data.related.length ? (
        <section className="border-t border-[#ece4d8] bg-white/60 py-14">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#9ba6ba]">Related packages</p>
                <h3 className="mt-2 font-homevilas text-4xl font-semibold text-[#0d213f]">Continue exploring</h3>
              </div>
              <Link
                to={listUrl}
                className="rounded-full border border-[#d4dbe6] px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600"
              >
                Back to listing
              </Link>
            </div>
            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {data.related.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  formatPrice={formatCurrency}
                  labels={{ enquire: 'Enquire now', viewDetails: 'View details', perPerson: 'per person' }}
                  onEnquire={() => setEnquiryPkg(pkg)}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <EnquiryModal
        open={Boolean(enquiryPkg)}
        onClose={() => setEnquiryPkg(null)}
        pkg={enquiryPkg}
        source="DETAILS"
        labels={{ enquire: 'Request package', perPerson: 'per person' }}
      />
      {isMobile ? <MobileBottomNav /> : null}
    </div>
  );
};

export default PackageDetail;
